"use client";

import { useState } from "react";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import Image from "@/lib/compat/image";
import { AttestorLogoSlot } from "@/components/attestors/AttestorLogoSlot";
import { LtrToken } from "@/components/cdm/LtrToken";
import { NetworkVignette } from "@/components/web/NetworkVignette";
import type { GraphState } from "@/components/web/types";
import type { DocumentRecord, DocumentLifecycleState } from "@/lib/sse/useSSE";
import { SignDocumentModal } from "@/components/sign/SignDocumentModal";

/**
 * Screen 2.2 — Document carousel. Six poultry-specific document tiles
 * walk through Created → Hashed → Attested → Transmitted → Validated.
 * Each Attested step lights up the corresponding attestation partner via
 * AttestorLogoSlot.
 */

export type DocSpec = {
  type: string;
  label: string;
  attestorOrbid?: string;
  attestorLabel?: string;
  pdfPath?: string;
  issuerName?: string;
  issuerOrg?: string;
};

export type HeaderAttestor = {
  /** Path to the attestor logo svg/png under /public/attestors/. */
  src: string;
  /** Alt text + the abbreviated chamber name shown next to the logo. */
  alt: string;
  /** Full origination caption, e.g. "Origination attested · Arab-Brazilian Chamber of Commerce (ABCC)". */
  caption: string;
};

export const POULTRY_DOCS: DocSpec[] = [
  { type: "CommercialInvoice",           label: "Commercial Invoice" },
  { type: "HalalSlaughterCertificate",   label: "Halal Slaughter Cert.",     attestorOrbid: "orbid:cdial-halal-br", attestorLabel: "CDIAL Halal",
    pdfPath: "/sign-docs/halal-certificate.pdf",
    issuerName: "CDIAL Halal",
    issuerOrg: "Centro de Divulgação do Islam · BR" },
  { type: "VeterinaryHealthCertificate", label: "Veterinary Health Cert.",   attestorOrbid: "orbid:mapa-export-br", attestorLabel: "MAPA",
    pdfPath: "/sign-docs/vet-certificate.pdf",
    issuerName: "MAPA",
    issuerOrg: "Ministério da Agricultura, Pecuária e Abastecimento · BR" },
  { type: "CertificateOfOrigin",         label: "Certificate of Origin",     attestorOrbid: "orbid:abcc-cert-br",   attestorLabel: "ABCC",
    pdfPath: "/sign-docs/certificate-of-origin.pdf",
    issuerName: "ABCC",
    issuerOrg: "Arab-Brazilian Chamber of Commerce · BR" },
  { type: "PackingList",                 label: "Packing List" },
  { type: "EBL",                         label: "Electronic Bill of Lading",
    pdfPath: "/sign-docs/ebl.pdf",
    issuerName: "Demo Liner Co.",
    issuerOrg: "Carrier (electronic original on Orbitra ledger)" },
];

// T4 air-cargo (chilled halal beef tenderloin) — same Halal + Veterinary
// chain as T1/T2 but the carrier doc is the Air Waybill (AWB) instead of
// the Electronic Bill of Lading. Operated by Qatar Airways Cargo on the
// Viracopos → Doha → Dubai route.
export const MEAT_AIR_DOCS: DocSpec[] = [
  { type: "CommercialInvoice",           label: "Commercial Invoice" },
  { type: "HalalSlaughterCertificate",   label: "Halal Slaughter Cert.",     attestorOrbid: "orbid:cdial-halal-br", attestorLabel: "CDIAL Halal",
    pdfPath: "/sign-docs/halal-certificate-t4.pdf",
    issuerName: "CDIAL Halal",
    issuerOrg: "Centro de Divulgação do Islam · BR" },
  { type: "VeterinaryHealthCertificate", label: "Veterinary Health Cert.",   attestorOrbid: "orbid:mapa-export-br", attestorLabel: "MAPA",
    pdfPath: "/sign-docs/vet-certificate-t4.pdf",
    issuerName: "MAPA",
    issuerOrg: "Ministério da Agricultura, Pecuária e Abastecimento · BR · meat-specific" },
  { type: "CertificateOfOrigin",         label: "Certificate of Origin",     attestorOrbid: "orbid:abcc-cert-br",   attestorLabel: "ABCC",
    pdfPath: "/sign-docs/certificate-of-origin-t4.pdf",
    issuerName: "ABCC",
    issuerOrg: "Arab-Brazilian Chamber of Commerce · BR" },
  { type: "PackingList",                 label: "Packing List" },
  { type: "AWB",                         label: "Air Waybill (AWB)",
    pdfPath: "/sign-docs/awb-t4.pdf",
    issuerName: "Qatar Airways Cargo",
    issuerOrg: "Carrier · MAWB 157-VCP-DXB · electronic original on Orbitra ledger" },
];

// T3 reverse-corridor (urea fertilizer) — different doc set, different
// attestor partners. Halal / Veterinary do not apply.
export const FERTILIZER_DOCS: DocSpec[] = [
  { type: "CommercialInvoice",             label: "Commercial Invoice" },
  { type: "BulkCargoCertificateOfQuality", label: "Bulk Cargo Cert. of Quality", attestorOrbid: "orbid:sgs-cargo-ae",   attestorLabel: "SGS Cargo",
    pdfPath: "/sign-docs/bulk-cargo-cert-t3.pdf",
    issuerName: "SGS",
    issuerOrg: "Independent surveyor · pre-shipment inspection at Jebel Ali" },
  { type: "PhytosanitaryCertificate",      label: "Phytosanitary Cert.",         attestorOrbid: "orbid:moccae-ae",      attestorLabel: "MOCCAE",
    pdfPath: "/sign-docs/phytosanitary-cert-t3.pdf",
    issuerName: "MOCCAE",
    issuerOrg: "UAE Ministry of Climate Change & Environment" },
  { type: "CertificateOfOrigin",           label: "Certificate of Origin",       attestorOrbid: "orbid:fed-uae-chambers", attestorLabel: "FUC",
    pdfPath: "/sign-docs/certificate-of-origin-t3.pdf",
    issuerName: "FUC",
    issuerOrg: "Federation of UAE Chambers" },
  { type: "PackingList",                   label: "Packing List" },
  { type: "EBL",                           label: "Electronic Bill of Lading",
    pdfPath: "/sign-docs/ebl-t3.pdf",
    issuerName: "Demo Liner Co.",
    issuerOrg: "Hapag-Lloyd Bulk · carrier (electronic original on Orbitra ledger)" },
];

const DEFAULT_HEADER_ATTESTOR: HeaderAttestor = {
  src: "/attestors/orbid-abcc-cert-br.svg",
  alt: "ABCC",
  caption: "Origination attested · Arab-Brazilian Chamber of Commerce (ABCC)",
};

const STATE_ORDER: DocumentLifecycleState[] = ["Created", "Hashed", "Attested", "Transmitted", "Validated"];

export function DocumentCarousel({
  documents,
  graph,
  docs = POULTRY_DOCS,
  headerAttestor = DEFAULT_HEADER_ATTESTOR,
  carouselSubtitle = "Document carousel · 6 poultry-specific tiles",
  className,
}: {
  documents: Record<string, DocumentRecord>;
  graph?: GraphState;
  /** Tile set to render. Defaults to POULTRY_DOCS (T1/T2). T3 passes FERTILIZER_DOCS. */
  docs?: DocSpec[];
  /** Origination-attestor chip rendered under the heading. */
  headerAttestor?: HeaderAttestor;
  /** Eyebrow line above the heading. */
  carouselSubtitle?: string;
  className?: string;
}) {
  const t = useTranslations("documentCarousel");
  const noDocsYet = Object.keys(documents).length === 0;
  const [openDocType, setOpenDocType] = useState<string | null>(null);
  const openDoc = docs.find((d) => d.type === openDocType) ?? null;

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{carouselSubtitle}</p>
          <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("title")}</h3>
          <p className="mt-1.5 inline-flex items-center gap-2 rounded-full border border-state-verified/40 bg-state-verified/8 py-0.5 pe-2.5 ps-1 font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">
            <span className="inline-flex h-5 w-12 items-center justify-center rounded-sm bg-orbitra-paper">
              <Image src={headerAttestor.src} alt={headerAttestor.alt} width={44} height={18} style={{ objectFit: "contain" }} />
            </span>
            {headerAttestor.caption}
          </p>
          {noDocsYet && (
            <p className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-muted">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-state-traced" />
              {t("noDocsPrefix")} <strong className="font-bold">Atlas Agent</strong> {t("noDocsSuffix")}
            </p>
          )}
        </div>
        <p className="text-[11px] text-orbitra-muted">{t("walkLabel")}</p>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-3 lg:grid-cols-6">
        {docs.map((d) => {
          const rec  = documents[d.type];
          const state = rec?.state ?? null;
          return (
            <DocTile
              key={d.type}
              label={d.label}
              type={d.type}
              issuer={rec?.issuer}
              state={state}
              attestorOrbid={d.attestorOrbid}
              attestorLabel={d.attestorLabel}
              attestorLei={rec?.attestorLei}
              onClick={d.pdfPath ? () => setOpenDocType(d.type) : undefined}
            />
          );
        })}
      </div>

      {openDoc?.pdfPath && (
        <SignDocumentModal
          id={`doc:${openDoc.type}`}
          open={openDocType === openDoc.type}
          onOpen={() => setOpenDocType(openDoc.type)}
          onClose={() => setOpenDocType(null)}
          pdfPath={openDoc.pdfPath}
          documentTitle={openDoc.label}
          documentRef={documents[openDoc.type]?.attestorLei}
          signedAs={openDoc.issuerName ?? openDoc.attestorLabel ?? "Issuer"}
          signerOrg={openDoc.issuerOrg ?? ""}
          viewOnly
        />
      )}

      <div className="glass flex items-center gap-3 px-4 py-2 text-[11px] text-orbitra-paper">
        <span className="font-mono text-[10px] font-bold text-orbitra-teal-soft">{t("atlasAgentLabel")}</span>
        <span>{t("anchoringPrefix")} <LtrToken className="text-state-traced">topic-0.4801203</LtrToken>. {t("anchoringSuffix")}</span>
      </div>

      {graph && <NetworkVignette graph={graph} preset="doc-attestation" />}
    </div>
  );
}

function DocTile({
  label,
  type,
  issuer,
  state,
  attestorOrbid,
  attestorLabel,
  attestorLei,
  onClick,
}: {
  label: string;
  type?: string;
  issuer?: string;
  state: DocumentLifecycleState | null;
  attestorOrbid?: string;
  attestorLabel?: string;
  attestorLei?: string;
  onClick?: () => void;
}) {
  const t = useTranslations("documentCarousel");
  const translatedLabel = t(`docLabel.${type ?? ""}`, { fallback: label });
  const STATE_LABELS: Record<string, string> = {
    Created:     t("stateCreated"),
    Hashed:      t("stateHashed"),
    Attested:    t("stateAttested"),
    Transmitted: t("stateTransmitted"),
    Validated:   t("stateValidated"),
  };
  const idx = state ? STATE_ORDER.indexOf(state) : -1;
  const interactive = !!onClick;
  const className = clsx(
    "glass flex flex-col gap-2 p-3 text-start transition-state",
    idx >= 4 && "border-policy-pass/40",
    idx === 2 && "shadow-[0_0_18px_var(--state-traced-glow)]",
    interactive && "cursor-pointer hover:-translate-y-px hover:border-state-verified/50 hover:shadow-[0_0_18px_var(--state-verified-glow)] focus:outline-none focus-visible:ring-2 focus-visible:ring-state-verified/60",
  );
  const body = (
    <>
      <div className="flex items-start justify-between gap-1">
        <p className="font-display text-[12px] font-semibold leading-tight text-orbitra-paper">{translatedLabel}</p>
        {interactive && (
          <span
            aria-hidden="true"
            className="rounded-full border border-state-verified/40 bg-state-verified/10 px-1.5 py-px font-mono text-[8px] uppercase tracking-wider text-state-verified"
          >
            {t("viewBadge")}
          </span>
        )}
      </div>
      {issuer && <p className="text-[9px] text-orbitra-muted">{issuer}</p>}

      {attestorOrbid && (
        <div className="mt-1 flex items-center gap-2 rounded-md border border-state-traced/20 bg-orbitra-ink/40 px-2 py-1.5">
          <AttestorLogoSlot partnerOrbId={attestorOrbid} fallbackName={attestorLabel ?? "Attestor"} size={20} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[10px] text-orbitra-paper">{attestorLabel}</span>
            {attestorLei && <span className="truncate font-mono text-[9px] text-orbitra-muted">{attestorLei}</span>}
          </div>
        </div>
      )}

      {/* MLETR Art. 10 evidence — only on the electronic Bill of Lading.
       *  Shows the three legal-equivalence properties (singularity,
       *  integrity, control) that make an eBL legally interchangeable
       *  with a paper bearer document under MLETR Art. 10. Hovers reveal
       *  the per-property explanation. */}
      {type === "EBL" && state && (
        <div
          className="mt-1 rounded-md border border-state-traced/30 bg-state-traced/8 px-2 py-1.5 text-[9px] text-state-traced cursor-help"
          title={t("mletrHelper")}
        >
          <p className="font-mono uppercase tracking-[0.14em]">{t("mletrChip")}</p>
          <ul className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-state-traced/90">
            <li>● {t("mletrSingularity")}</li>
            <li>● {t("mletrIntegrity")}</li>
            <li>● {t("mletrControl")}</li>
          </ul>
        </div>
      )}

      <div className="mt-auto flex flex-col gap-1">
        {STATE_ORDER.map((s, i) => (
          <span
            key={s}
            className={clsx(
              "flex items-center gap-1.5 rounded text-[9px] uppercase tracking-wider transition-colors duration-state",
              i <= idx ? "text-state-traced" : "text-orbitra-muted/50",
            )}
          >
            <span className={clsx(
              "h-1.5 w-1.5 rounded-full",
              i <= idx ? "bg-state-traced shadow-[0_0_6px_var(--state-traced)]" : "bg-orbitra-muted/30",
            )} />
            {STATE_LABELS[s] ?? s}
          </span>
        ))}
      </div>
    </>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className={className} aria-label={t("openDocAriaLabel", { label: translatedLabel })}>
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}
